import React, { useEffect, useState } from "react";
import {
  Modal,
  Checkbox,
  List,
  message,
  Spin,
  Select,
  Card,
  Divider,
  Typography,
} from "antd";
import ReactQuill from "react-quill"; // Import React Quill
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import { useDispatch, useSelector } from "react-redux";
import { fetchEligibleTranslators, sendBid } from "../projectSlice";

const { Option } = Select;
const { Title, Paragraph } = Typography;

const BidModal = ({ project, visible, onClose }) => {
  const dispatch = useDispatch();
  const { eligibleTranslators } = useSelector((state) => state.projects);

  const [selectedBids, setSelectedBids] = useState({});
  // New state: store a note for each translator (keyed by translator ID)
  const [translatorNotes, setTranslatorNotes] = useState({});
  const [loading, setLoading] = useState(false); // For fetching eligible translators
  const [sendingBids, setSendingBids] = useState(false); // For sending bids

  useEffect(() => {
    if (visible && project?.id) {
      setLoading(true);
      dispatch(fetchEligibleTranslators(project.id))
        .unwrap()
        .finally(() => setLoading(false));
    }
  }, [visible, project?.id, dispatch]);

  const handleServiceCheckboxChange = (translatorId, serviceId, checked) => {
    setSelectedBids((prev) => {
      const translatorBids = { ...(prev[translatorId] || {}) };
      if (checked) {
        translatorBids[serviceId] = translatorBids[serviceId] || [];
      } else {
        delete translatorBids[serviceId];
      }
      return { ...prev, [translatorId]: translatorBids };
    });
  };

  const handleTargetLanguagesChange = (translatorId, serviceId, values) => {
    setSelectedBids((prev) => {
      const translatorBids = { ...(prev[translatorId] || {}) };
      translatorBids[serviceId] = values;
      return { ...prev, [translatorId]: translatorBids };
    });
  };

  // Update the note for a specific translator
  const handleNoteChange = (translatorId, note) => {
    setTranslatorNotes((prev) => ({ ...prev, [translatorId]: note }));
  };

  const handleSendBids = async () => {
    if (Object.keys(selectedBids).length === 0) {
      message.error("Please select at least one translator and service.");
      return;
    }

    // Validate that each selected service has target languages selected
    for (const [translatorId, services] of Object.entries(selectedBids)) {
      for (const [serviceId, targetLanguages] of Object.entries(services)) {
        if (!targetLanguages || targetLanguages.length === 0) {
          message.error(
            `Please select target languages for service ${serviceId} for translator ${translatorId}.`
          );
          return;
        }
      }
    }

    try {
      setSendingBids(true);
      await Promise.all(
        Object.entries(selectedBids).map(([translatorId, servicesObj]) => {
          const servicesArray = Object.entries(servicesObj).map(
            ([serviceId, target_language_ids]) => ({
              service_id: parseInt(serviceId, 10),
              target_language_ids: target_language_ids.map((id) =>
                parseInt(id, 10)
              ),
            })
          );
          // Get the note for this translator; default to an empty string if none
          const note = translatorNotes[translatorId] || "";
          return dispatch(
            sendBid({
              projectId: project.id,
              translatorId: parseInt(translatorId, 10),
              services: servicesArray,
              note, // Send the translator-specific note
            })
          );
        })
      );
      message.success("Bids sent successfully.");
      onClose();
    } catch (error) {
      message.error(error.message || "Failed to send bids.");
    } finally {
      setSendingBids(false);
    }
  };

  const translatorsList =
    eligibleTranslators[project?.id]?.eligible_translators || [];

  return (
    <Modal
      visible={visible}
      title="Send Bids to Translators"
      onCancel={onClose}
      onOk={handleSendBids}
      okText={sendingBids ? "Sending..." : "Send Bids"}
      okButtonProps={{ disabled: sendingBids }}
      width={800}
    >
      <Card style={{ marginBottom: 16, backgroundColor: "#fafafa" }}>
        <Title level={5}>Instructions</Title>
        <Paragraph>
          For each translator, select the services you wish to bid on and choose the target languages for each service.
        </Paragraph>
      </Card>
      {loading ? (
        <Spin style={{ display: "block", textAlign: "center" }} />
      ) : (
        <List
          bordered
          dataSource={translatorsList}
          locale={{ emptyText: "No eligible translators found." }}
          renderItem={(translator) => (
            <Card key={translator.id} title={translator.name} style={{ marginBottom: 16 }} size="small">
              {project?.services?.map((service) => {
                const isChecked =
                  selectedBids[translator.id] &&
                  selectedBids[translator.id][service.id] !== undefined;
                return (
                  <div key={service.id} style={{ marginBottom: 12 }}>
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) =>
                        handleServiceCheckboxChange(translator.id, service.id, e.target.checked)
                      }
                    >
                      {service.name}
                    </Checkbox>
                    {isChecked && (
                      <div style={{ marginLeft: 24, marginTop: 4 }}>
                        <span style={{ marginRight: 8 }}>Target Languages:</span>
                        <Select
                          mode="multiple"
                          style={{ width: "60%" }}
                          placeholder="Select target languages"
                          value={selectedBids[translator.id][service.id]}
                          onChange={(values) =>
                            handleTargetLanguagesChange(translator.id, service.id, values)
                          }
                        >
                          {project?.target_languages?.map((lang) => (
                            <Option key={lang.id} value={lang.id}>
                              {lang.name} ({lang.code})
                            </Option>
                          ))}
                        </Select>
                      </div>
                    )}
                    <Divider style={{ margin: "8px 0" }} />
                  </div>
                );
              })}
              {/* Translator-specific note input */}
              <Card type="inner" title="Additional Bid Note" style={{ marginTop: 12 }}>
                <ReactQuill
                  value={translatorNotes[translator.id] || ""}
                  onChange={(content) => handleNoteChange(translator.id, content)}
                  placeholder="Enter any additional note for this translator..."
                />
              </Card>
            </Card>
          )}
        />
      )}
      {sendingBids && (
        <Spin
          style={{ display: "block", textAlign: "center", marginTop: 16 }}
          tip="Sending bids..."
        />
      )}
    </Modal>
  );
};

export default BidModal;
